<div class="biometric-auth">

  <!-- stage 1 — context + start -->
  @if (stage === 'intro') {
    <div class="fa-stage">
      @if (authMode === 'FINGERPRINT') {
        <p class="fa-lede">{{ 'field.label.biometric_auth_intro_fingerprint' | translate | async }}</p>
      } @else if (authMode === 'IRIS') {
        <p class="fa-lede">{{ 'field.label.biometric_auth_intro_iris' | translate | async }}</p>
      } @else {
        <p class="fa-lede">{{ 'field.label.biometric_auth_intro_face' | translate | async }}</p>
      }
      <ul class="fa-context">
        @if (data.patient_name) {
          <li><span>{{ 'field.label.patient_name' | translate | async }}</span><b>{{ data.patient_name }}</b></li>
        }
        @if (data.uhId) {
          <li><span>{{ 'field.label.uhId' | translate | async }}</span><b>{{ data.uhId }}</b></li>
        }
        @if (isLocalCapture && data.abhaNumber) {
          <li><span>{{ 'field.label.abha_number' | translate | async }}</span><b>{{ data.abhaNumber }}</b></li>
        }
        @if (data.payerName || data.payerCode) {
          <li><span>{{ 'field.label.payerName' | translate | async }}</span><b>{{ data.payerName || data.payerCode }}</b></li>
        }
      </ul>

      <!-- discovery runs before anything is spent at ABDM, so its failure is shown here -->
      @if (busy) {
        <p class="fa-status"><i class="fa fa-spinner fa-spin"></i> <span>{{ busyMessage }}</span></p>
      }
      @if (errorMessage) {
        <p class="fa-error"><i class="fa fa-exclamation-triangle"></i> {{ errorMessage }}</p>
      }

      <div class="fa-actions">
        <button type="button" class="secondary-btn" (click)="cancel()">{{ 'button.cancel' | translate | async }}</button>
        @if (isLocalCapture) {
          <button type="button" class="primary-btn" [disabled]="busy" (click)="discoverDevice()">
            {{ 'button.detect_device' | translate | async }}
          </button>
        } @else {
          <button type="button" class="primary-btn" (click)="startAuthentication()">
            {{ 'button.start_biometric_auth' | translate | async }}
          </button>
        }
      </div>
    </div>
  }

  <!-- stage 2a (face) — QR on screen, beneficiary scanning on their phone -->
  @if (stage === 'awaiting') {
    <div class="fa-stage">
      <p class="fa-lede">{{ 'field.label.face_auth_scan_instruction' | translate | async }}</p>
      <div class="fa-qr" id="biometric-auth-qr"></div>
      <p class="fa-status">
        @if (polling) {
          <i class="fa fa-spinner fa-spin"></i>
          <span>{{ 'field.label.face_auth_awaiting_scan' | translate | async }}</span>
        }
        @if (timedOut) {
          <span class="fa-error">{{ errorMessage }}</span>
        }
      </p>

      <!-- The txnId is the only handle ABDM support has on this attempt, so it is on screen
           from the moment it exists rather than only once the scan has been verified. -->
      <ul class="fa-context fa-txn">
        <li><span>{{ 'field.label.biometric_auth_txn_id' | translate | async }}</span><b>{{ biometricAuth.txnId }}</b></li>
        <li><span>{{ 'field.label.biometric_auth_started_at' | translate | async }}</span><b>{{ startedAtLabel }} ({{ elapsedLabel }})</b></li>
        @if (polling) {
          <li><span>{{ 'field.label.biometric_auth_time_left' | translate | async }}</span><b>{{ waitRemainingLabel }}</b></li>
        }
      </ul>
      <div class="fa-actions">
        <button type="button" class="secondary-btn" (click)="cancel()">{{ 'button.cancel' | translate | async }}</button>
        @if (timedOut) {
          <button type="button" class="primary-btn" (click)="restart()">{{ 'button.retry' | translate | async }}</button>
        } @else {
          <button type="button" class="primary-btn" (click)="checkStatus(false)">
            {{ 'button.check_now' | translate | async }}
          </button>
        }
      </div>
    </div>
  }

  <!-- stage 2b (fingerprint / iris) — the detected device, named before anyone is scanned -->
  @if (stage === 'device') {
    <div class="fa-stage">
      @if (device) {
        <p class="fa-lede fa-ok">
          <i class="fa fa-check-circle"></i>
          {{ 'field.label.biometric_auth_device_detected' | translate | async }}
        </p>
        <ul class="fa-context">
          <li>
            <span>{{ 'field.label.biometric_auth_device_model' | translate | async }}</span>
            <b>{{ device.model || device.info || ('localhost:' + device.port) }}</b>
          </li>
          @if (device.serial) {
            <li><span>{{ 'field.label.biometric_auth_device_serial' | translate | async }}</span><b>{{ device.serial }}</b></li>
          }
          @if (device.rdsVer) {
            <li><span>{{ 'field.label.biometric_auth_device_version' | translate | async }}</span><b>{{ device.rdsVer }}</b></li>
          }
          <li><span>{{ 'field.label.biometric_auth_device_status' | translate | async }}</span><b>{{ device.status }}</b></li>
        </ul>
        @if (authMode === 'IRIS') {
          <p class="fa-lede">{{ 'field.label.biometric_auth_place_iris' | translate | async }}</p>
        } @else {
          <p class="fa-lede">{{ 'field.label.biometric_auth_place_finger' | translate | async }}</p>
        }
        <p class="fa-note">{{ 'field.label.biometric_auth_capture_note' | translate | async }}</p>
      } @else {
        <p class="fa-error">{{ 'field.label.biometric_auth_no_device' | translate | async }}</p>
      }

      @if (errorMessage) {
        <p class="fa-error"><i class="fa fa-exclamation-triangle"></i> {{ errorMessage }}</p>
      }

      <div class="fa-actions">
        <button type="button" class="secondary-btn" (click)="cancel()">{{ 'button.cancel' | translate | async }}</button>
        <button type="button" class="secondary-btn" [disabled]="busy" (click)="retryDiscovery()">
          {{ 'button.detect_again' | translate | async }}
        </button>
        <button type="button" class="primary-btn" [disabled]="busy || !device" (click)="captureAndVerify()">
          {{ 'button.capture' | translate | async }}
        </button>
      </div>
    </div>
  }

  <!-- stage 3a (fingerprint / iris) — init, scan and verify, all in one gesture -->
  @if (stage === 'capturing') {
    <div class="fa-stage">
      <p class="fa-status fa-capturing">
        <i class="fa fa-spinner fa-spin"></i>
        <span>{{ busyMessage }}</span>
      </p>
      <p class="fa-note">{{ 'field.label.biometric_auth_capture_note' | translate | async }}</p>
      @if (hasTransaction) {
        <ul class="fa-context fa-txn">
          <li><span>{{ 'field.label.biometric_auth_txn_id' | translate | async }}</span><b>{{ biometricAuth.txnId }}</b></li>
          <li><span>{{ 'field.label.biometric_auth_started_at' | translate | async }}</span><b>{{ startedAtLabel }} ({{ elapsedLabel }})</b></li>
        </ul>
      }
      <div class="fa-actions">
        <button type="button" class="secondary-btn" (click)="cancel()">{{ 'button.cancel' | translate | async }}</button>
      </div>
    </div>
  }

  <!-- stage 3b (face) — scan captured, collect Aadhaar for verification -->
  @if (stage === 'captured') {
    <div class="fa-stage">
      <p class="fa-lede fa-ok">
        <i class="fa fa-check-circle"></i>
        {{ 'field.label.biometric_auth_captured' | translate | async }}
      </p>
      <ul class="fa-context fa-txn">
        <li><span>{{ 'field.label.biometric_auth_txn_id' | translate | async }}</span><b>{{ biometricAuth.txnId }}</b></li>
        <li><span>{{ 'field.label.biometric_auth_started_at' | translate | async }}</span><b>{{ startedAtLabel }} ({{ elapsedLabel }})</b></li>
      </ul>
      <div class="kx-control">
        <label for="biometric-auth-aadhaar">{{ 'field.label.aadhaar_number' | translate | async }}<span class="req">*</span></label>
        <!-- Masked by default, but revealable: the operator is copying 12 digits off the
             beneficiary's card and cannot proof-read them as dots. -->
        <div class="fa-reveal">
          <!-- autocomplete="new-password", not "off": this is a type=password field, and the
               browser's password manager fills those from saved credentials no matter what "off"
               says. "new-password" is the opt-out it honours, and it also suppresses the
               "save password?" prompt for a number that is never ours to store. -->
          <input id="biometric-auth-aadhaar" [type]="aadhaarVisible ? 'text' : 'password'"
                 class="form-control" inputmode="numeric"
                 maxlength="12" autocomplete="new-password" [(ngModel)]="aadhaar"
                 placeholder="{{ 'field.hint.aadhaar_number' | translate | async }}" />
          <button type="button" class="fa-reveal-btn" [attr.aria-pressed]="aadhaarVisible"
                  (click)="aadhaarVisible = !aadhaarVisible">
            <i class="fa" [class.fa-eye]="!aadhaarVisible" [class.fa-eye-slash]="aadhaarVisible"></i>
          </button>
        </div>
        <small class="fa-note">{{ 'field.label.aadhaar_not_stored' | translate | async }}</small>
      </div>
      <div class="kx-control">
        <label for="biometric-auth-mobile">{{ 'field.label.mobile' | translate | async }}</label>
        <input id="biometric-auth-mobile" type="text" class="form-control" inputmode="numeric"
               maxlength="10" autocomplete="off" [(ngModel)]="mobile" />
      </div>
      @if (errorMessage) {
        <p class="fa-error">{{ errorMessage }}</p>
      }
      <div class="fa-actions">
        <button type="button" class="secondary-btn" (click)="cancel()">{{ 'button.cancel' | translate | async }}</button>
        <button type="button" class="primary-btn" [disabled]="!isAadhaarValid()" (click)="verify()">
          {{ 'button.verify' | translate | async }}
        </button>
      </div>
    </div>
  }

  <!-- stage 4 — verified, show the ABHA profile back as confirmation -->
  @if (stage === 'verified') {
    <div class="fa-stage">
      <p class="fa-lede fa-ok">
        <i class="fa fa-check-circle"></i>
        {{ 'field.label.biometric_auth_verified' | translate | async }}
      </p>
      <ul class="fa-context">
        @if (verifiedName) {
          <li><span>{{ 'field.label.patient_name' | translate | async }}</span><b>{{ verifiedName }}</b></li>
        }
        @if (verifiedAbhaNumber) {
          <li><span>{{ 'field.label.abha_number' | translate | async }}</span><b>{{ verifiedAbhaNumber }}</b></li>
        }
        <li><span>{{ 'field.label.biometric_auth_txn_id' | translate | async }}</span><b>{{ biometricAuth.txnId }}</b></li>
        <li><span>{{ 'field.label.biometric_auth_verified_at' | translate | async }}</span><b>{{ verifiedAtLabel }}</b></li>
        @if (biometricAuth.tokenExpiresAt) {
          <li>
            <span>{{ 'field.label.biometric_auth_valid_for' | translate | async }}</span>
            <b [class.fa-error]="tokenExpired">{{ tokenExpired ? ('field.label.biometric_auth_expired' | translate | async) : validForLabel }}</b>
          </li>
        }
      </ul>
      @if (tokenExpired) {
        <p class="fa-error">
          <i class="fa fa-exclamation-triangle"></i>
          {{ 'field.label.biometric_auth_expired_note' | translate | async }}
        </p>
      }
      <!-- Cyclic capture only: optional evidence filed against this one treatment day. Absent on
           the pre-auth and claim authentications, which carry their own consent-form fields. -->
      @if (isCycleCapture) {
        <div class="fa-cycle-doc">
          <label class="fa-cycle-doc-label">
            {{ 'field.label.service_period' | translate | async }}
            <span class="fa-required">*</span>
          </label>
          <input type="text" ngxDaterangepickerMd
            class="form-control choose-date datepicker-search"
            placeholder="{{ 'field.label.service_period' | translate | async }}"
            [showCancel]="true"
            [singleDatePicker]="false"
            [timePicker]="true"
            [timePickerSeconds]="false"
            [timePicker24Hour]="false"
            [autoApply]="true"
            [showDropdowns]="true"
            [opens]="'center'"
            [locale]="{ format: 'DD/MM/YYYY hh:mm A' }"
            [(ngModel)]="cyclicPeriod" />

          <label class="fa-cycle-doc-label">
            {{ 'field.label.cyclic_doc' | translate | async }}
            <em>{{ 'field.label.optional' | translate | async }}</em>
          </label>
          <dhs-file-uploader
            [id]="'cyclic_doc'"
            [multiple]="false"
            [formatsAllowed]="'.jpg,.jpeg,.png,.pdf,.doc,.docx'"
            [maxSize]="5"
            [resource]="'orgImage'"
            [question]="cyclicDocQuestion"
            [(ngModel)]="cyclicDoc"
            (documentInfo)="onCyclicDocUploaded($event)"
            (delete)="onCyclicDocRemoved()">
          </dhs-file-uploader>
          <!-- Pre-filled with the uploaded file's name and freely editable; it becomes the
               Attachment.title of this cycle's document on the claim. -->
          @if (cyclicDoc) {
            <label class="fa-cycle-doc-label" for="cyclic_doc_title">
              {{ 'field.label.document_title' | translate | async }}
            </label>
            <input type="text" id="cyclic_doc_title" class="form-control" maxlength="256"
              placeholder="{{ 'field.label.document_title' | translate | async }}"
              [(ngModel)]="cyclicDocTitle" />
          }
        </div>
      }
      <p class="fa-note">{{ 'field.label.biometric_auth_expiry_note' | translate | async }}</p>
      <div class="fa-actions">
        @if (tokenExpired) {
          <!-- Handing a dead txnId back to the form would only be rejected at the payer, so the
               only way out of an expired authentication is a fresh one. -->
          <button type="button" class="secondary-btn" (click)="cancel()">{{ 'button.cancel' | translate | async }}</button>
          <button type="button" class="primary-btn" (click)="restart()">{{ 'button.retry' | translate | async }}</button>
        } @else {
          <button type="button" class="primary-btn" (click)="done()">{{ 'button.done' | translate | async }}</button>
        }
      </div>
    </div>
  }

  <!-- failure -->
  @if (stage === 'failed') {
    <div class="fa-stage">
      <p class="fa-error"><i class="fa fa-exclamation-triangle"></i> {{ errorMessage }}</p>
      <div class="fa-actions">
        <button type="button" class="secondary-btn" (click)="cancel()">{{ 'button.cancel' | translate | async }}</button>
        <button type="button" class="primary-btn" (click)="restart()">{{ 'button.retry' | translate | async }}</button>
      </div>
    </div>
  }

</div>
